import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Cloud, Sun, CloudRain, Wind, Droplets, Thermometer, MapPin, RefreshCw,
  Sunrise, Sunset, Gauge, Eye, Settings, Search, Locate, Check, X, Clock,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import {
  resolveLocation, requestBrowserGeolocation, reverseGeocode, searchPlaces,
  getRecentLocations, pushRecentLocation, saveLocation, clearSavedLocation, getSavedLocation,
} from '../lib/weatherLocation';

/**
 * WeatherWidget
 *
 * Resolves the user's location via:
 *   1. saved preference (localStorage)
 *   2. browser geolocation
 *   3. IP-based approximation
 *   4. London fallback
 *
 * Exposes a Settings panel where the user can:
 *   - tap "Use my location" (forces fresh GPS prompt)
 *   - search and pick a place by name
 *   - pick from recently used locations
 *   - save the active location as a preference
 *   - clear the saved preference
 *
 * Last-updated timestamp + manual refresh button included.
 */

const WMO_DESCRIPTIONS = {
  0: 'Clear sky', 1: 'Mainly clear', 2: 'Partly cloudy', 3: 'Overcast',
  45: 'Foggy', 48: 'Depositing rime fog',
  51: 'Light drizzle', 53: 'Moderate drizzle', 55: 'Dense drizzle',
  61: 'Slight rain', 63: 'Moderate rain', 65: 'Heavy rain',
  71: 'Slight snow', 73: 'Moderate snow', 75: 'Heavy snow',
  80: 'Rain showers', 81: 'Moderate showers', 82: 'Heavy showers',
  95: 'Thunderstorm',
};

const getWeatherIcon = (code, size = 'w-8 h-8') => {
  if (code === 0) return <Sun className={`${size} text-yellow-400`} />;
  if (code <= 3) return <Cloud className={`${size} text-gray-400`} />;
  if (code >= 51 && code <= 67) return <CloudRain className={`${size} text-blue-400`} />;
  if (code >= 71 && code <= 77) return <Cloud className={`${size} text-white`} />;
  if (code >= 80 && code <= 82) return <CloudRain className={`${size} text-blue-500`} />;
  if (code >= 95) return <CloudRain className={`${size} text-purple-400`} />;
  return <Cloud className={`${size} text-gray-400`} />;
};

const formatTime = (iso) => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
const formatLastUpdated = (date) => date ? date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : null;

const sourceMeta = {
  gps:     { label: 'GPS',       tone: 'text-green-400' },
  saved:   { label: 'Saved',     tone: 'text-[#c8b273]' },
  ip:      { label: 'Approx.',   tone: 'text-yellow-400' },
  default: { label: 'Fallback',  tone: 'text-[#8a948a]' },
  manual:  { label: 'Manual',    tone: 'text-[#c8b273]' },
};

const WeatherWidget = ({ expanded = false }) => {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [location, setLocation] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [recents, setRecents] = useState([]);
  const [savedPref, setSavedPref] = useState(null);
  const searchTimer = useRef(null);

  // Boot — resolve once on mount.
  useEffect(() => {
    refreshLocationAndWeather({ preferGps: false });
    setRecents(getRecentLocations());
    setSavedPref(getSavedLocation());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- core flow ----
  const refreshLocationAndWeather = async ({ preferGps = false } = {}) => {
    setLoading(true);
    setError(null);
    const loc = await resolveLocation({ preferGps });
    setLocation(loc);
    await fetchWeatherFor(loc);
  };

  const fetchWeatherFor = async (loc) => {
    try {
      const r = await axios.get(
        `https://api.open-meteo.com/v1/forecast?latitude=${loc.latitude}&longitude=${loc.longitude}&current=temperature_2m,relative_humidity_2m,apparent_temperature,precipitation,weather_code,wind_speed_10m,wind_direction_10m,pressure_msl,cloud_cover,visibility&hourly=temperature_2m,precipitation_probability,weather_code&daily=temperature_2m_max,temperature_2m_min,precipitation_probability_max,sunrise,sunset,uv_index_max,wind_speed_10m_max&timezone=auto&forecast_days=7`,
      );
      setWeather(r.data);
      setLastUpdated(new Date());
    } catch (_) {
      setError('Failed to fetch weather data');
    } finally {
      setLoading(false);
    }
  };

  // ---- settings actions ----
  const useMyLocation = async () => {
    setError(null);
    setLoading(true);
    try {
      const { latitude, longitude } = await requestBrowserGeolocation();
      const place = await reverseGeocode(latitude, longitude);
      const loc = { latitude, longitude, ...place, source: 'gps' };
      setLocation(loc);
      pushRecentLocation(loc);
      setRecents(getRecentLocations());
      await fetchWeatherFor(loc);
    } catch (err) {
      // Surface a friendly message; the underlying widget keeps showing
      // whatever location we previously resolved.
      setError(err.code === 1
        ? 'Location permission denied. Search for your area below, or enable location in your browser settings.'
        : 'Could not get your location. Try searching for your area below.');
      setLoading(false);
    }
  };

  const pickPlace = async (place) => {
    setSearchQuery('');
    setSearchResults([]);
    const loc = { ...place, source: 'manual' };
    setLocation(loc);
    pushRecentLocation(loc);
    setRecents(getRecentLocations());
    setShowSettings(false);
    await fetchWeatherFor(loc);
  };

  const persistCurrent = () => {
    if (!location) return;
    saveLocation(location);
    setSavedPref(getSavedLocation());
  };

  const forgetSaved = () => {
    clearSavedLocation();
    setSavedPref(null);
  };

  // ---- debounced search ----
  const onSearchChange = (val) => {
    setSearchQuery(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (val.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      const rows = await searchPlaces(val);
      setSearchResults(rows);
      setSearching(false);
    }, 300);
  };

  const fishingCondition = (() => {
    if (!weather?.current) return { status: 'Unknown', color: 'text-gray-400', tip: '' };
    const { wind_speed_10m, pressure_msl, cloud_cover, weather_code } = weather.current;
    let score = 50;
    if (wind_speed_10m < 5) score += 10;
    else if (wind_speed_10m <= 15) score += 20;
    else if (wind_speed_10m <= 25) score += 5;
    else score -= 15;
    if (pressure_msl >= 1015 && pressure_msl <= 1025) score += 15;
    else if (pressure_msl < 1010) score += 10;
    if (cloud_cover >= 60 && cloud_cover <= 90) score += 10;
    else if (cloud_cover >= 40) score += 5;
    if (weather_code >= 95) score -= 20;
    else if (weather_code >= 65) score -= 10;
    if (score >= 75) return { status: 'Excellent', color: 'text-green-400', tip: 'Perfect conditions! The carp should be feeding well.', score };
    if (score >= 60) return { status: 'Good',      color: 'text-[#c8b273]', tip: 'Solid conditions for a session. Focus on windward banks.', score };
    if (score >= 45) return { status: 'Fair',      color: 'text-orange-400', tip: 'Mixed conditions. Try adjusting your approach.', score };
    return                  { status: 'Challenging', color: 'text-red-400',   tip: 'Tough conditions. Consider waiting for improvement.', score };
  })();

  const getWindDirection = (deg) => ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'][Math.round(deg / 45) % 8];

  if (loading && !weather) {
    return (
      <div className="glass-card p-6 animate-pulse" data-testid="weather-skeleton">
        <div className="h-8 bg-[#141714] rounded w-1/3 mb-4" />
        <div className="h-24 bg-[#141714] rounded mb-4" />
        <div className="h-16 bg-[#141714] rounded" />
      </div>
    );
  }

  if (error && !weather) {
    return (
      <div className="glass-card p-6 text-center" data-testid="weather-error">
        <Cloud className="w-12 h-12 text-[#4a504a] mx-auto mb-4" />
        <p className="text-[#8a948a]">{error}</p>
        <Button variant="ghost" className="btn-ghost mt-4" onClick={() => refreshLocationAndWeather({ preferGps: false })}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  const src = sourceMeta[location?.source] || sourceMeta.default;
  const displayName = location?.name || 'Your location';
  const subLabel = location?.region && location.region !== displayName ? `${location.region}, ${location.country || ''}` : (location?.country || '');

  return (
    <motion.div
      className="glass-card overflow-hidden"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="weather-widget"
    >
      {/* Header */}
      <div className="p-4 border-b border-white/5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <MapPin className="w-4 h-4 text-[#c8b273] flex-shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-white font-bold truncate" data-testid="weather-location-name">{displayName}</span>
              <span className={`text-[10px] uppercase tracking-wider ${src.tone}`} data-testid="weather-location-source">
                {src.label}
              </span>
            </div>
            {subLabel && <p className="text-[#6b7d6b] text-[11px] truncate">{subLabel}</p>}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowSettings((v) => !v)}
            className={`p-1.5 rounded transition-colors ${showSettings ? 'bg-[#c8b273]/15 text-[#c8b273]' : 'text-[#8a948a] hover:text-white'}`}
            data-testid="weather-settings-toggle"
            aria-label="Weather location settings"
          >
            <Settings className="w-4 h-4" />
          </button>
          <button
            onClick={() => fetchWeatherFor(location)}
            className="p-1.5 text-[#8a948a] hover:text-white transition-colors"
            data-testid="weather-refresh-btn"
            aria-label="Refresh weather"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Location settings panel */}
      {showSettings && (
        <div className="px-4 pt-4 pb-3 bg-[#0a0c0a] border-b border-white/5" data-testid="weather-settings-panel">
          <div className="flex flex-wrap gap-2 mb-3">
            <Button
              onClick={useMyLocation}
              className="btn-gold flex-1"
              size="sm"
              data-testid="weather-use-my-location-btn"
            >
              <Locate className="w-4 h-4 mr-2" /> Use my location
            </Button>
            <Button
              onClick={persistCurrent}
              variant="outline"
              size="sm"
              className="border-white/20 text-white"
              disabled={!location || (savedPref && savedPref.latitude === location.latitude && savedPref.longitude === location.longitude)}
              data-testid="weather-save-pref-btn"
            >
              <Check className="w-4 h-4 mr-2" /> Save as preferred
            </Button>
            {savedPref && (
              <Button
                onClick={forgetSaved}
                variant="ghost"
                size="sm"
                className="text-[#8a948a]"
                data-testid="weather-forget-pref-btn"
              >
                <X className="w-4 h-4 mr-2" /> Forget saved
              </Button>
            )}
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6b7d6b]" />
            <Input
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search town, city or lake area..."
              className="pl-9"
              data-testid="weather-search-input"
            />
          </div>

          {(searchResults.length > 0 || searching) && (
            <div className="mt-2 max-h-56 overflow-y-auto rounded border border-[#1f221f] bg-[#0b0d0b]" data-testid="weather-search-results">
              {searching && <p className="px-3 py-2 text-xs text-[#8a948a]">Searching...</p>}
              {searchResults.map((p) => (
                <button
                  key={p.id}
                  onClick={() => pickPlace(p)}
                  className="w-full text-left px-3 py-2 hover:bg-[#141714] text-sm text-white border-b border-[#141714] last:border-0"
                  data-testid={`weather-search-result-${p.id}`}
                >
                  <span className="font-bold">{p.name}</span>
                  {(p.region || p.country) && (
                    <span className="text-[#8a948a] text-xs ml-2">{[p.region, p.country].filter(Boolean).join(', ')}</span>
                  )}
                </button>
              ))}
            </div>
          )}

          {recents.length > 0 && searchResults.length === 0 && !searching && (
            <div className="mt-3" data-testid="weather-recents">
              <p className="text-[10px] uppercase tracking-wider text-[#8a948a] mb-1.5">Recent</p>
              <div className="flex flex-wrap gap-1.5">
                {recents.map((r, i) => (
                  <button
                    key={`${r.latitude}-${r.longitude}-${i}`}
                    onClick={() => pickPlace(r)}
                    className="text-xs px-2 py-1 bg-[#141714] border border-[#1f221f] rounded text-[#c0c4c0] hover:border-[#c8b273] hover:text-white"
                    data-testid={`weather-recent-${i}`}
                  >
                    <Clock className="inline w-3 h-3 mr-1 -mt-0.5 text-[#6b7d6b]" />
                    {r.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {error && (
            <p className="text-xs text-yellow-400 mt-3" data-testid="weather-settings-error">{error}</p>
          )}
        </div>
      )}

      {/* Current Conditions */}
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-end gap-2">
              <span className="font-['Barlow_Condensed'] text-6xl font-bold text-white" data-testid="weather-temp">
                {Math.round(weather.current.temperature_2m)}°
              </span>
              <span className="text-[#8a948a] text-lg mb-2">C</span>
            </div>
            <p className="text-[#c8b273] text-lg mt-1">
              {WMO_DESCRIPTIONS[weather.current.weather_code] || 'Unknown'}
            </p>
            <p className="text-[#8a948a] text-sm">
              Feels like {Math.round(weather.current.apparent_temperature)}°C
            </p>
          </div>
          <div className="text-right">
            {getWeatherIcon(weather.current.weather_code, 'w-20 h-20')}
          </div>
        </div>

        {/* Fishing Conditions */}
        <div className="bg-[#141714] rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[#8a948a] text-sm uppercase tracking-wider">Fishing Conditions</span>
            <span className={`font-['Barlow_Condensed'] text-xl font-bold ${fishingCondition.color}`}>
              {fishingCondition.status}
            </span>
          </div>
          <p className="text-[#8a948a] text-sm">{fishingCondition.tip}</p>
        </div>

        {/* Current Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-[#141714] rounded-lg p-3 text-center">
            <Wind className="w-5 h-5 text-[#6b7d6b] mx-auto mb-1" />
            <p className="text-white font-bold">{Math.round(weather.current.wind_speed_10m)} km/h</p>
            <p className="text-[#8a948a] text-xs">{getWindDirection(weather.current.wind_direction_10m)} Wind</p>
          </div>
          <div className="bg-[#141714] rounded-lg p-3 text-center">
            <Droplets className="w-5 h-5 text-[#6b7d6b] mx-auto mb-1" />
            <p className="text-white font-bold">{weather.current.relative_humidity_2m}%</p>
            <p className="text-[#8a948a] text-xs">Humidity</p>
          </div>
          <div className="bg-[#141714] rounded-lg p-3 text-center">
            <Gauge className="w-5 h-5 text-[#6b7d6b] mx-auto mb-1" />
            <p className="text-white font-bold">{Math.round(weather.current.pressure_msl)}</p>
            <p className="text-[#8a948a] text-xs">Pressure hPa</p>
          </div>
          <div className="bg-[#141714] rounded-lg p-3 text-center">
            <Eye className="w-5 h-5 text-[#6b7d6b] mx-auto mb-1" />
            <p className="text-white font-bold">{Math.round(weather.current.visibility / 1000)}km</p>
            <p className="text-[#8a948a] text-xs">Visibility</p>
          </div>
        </div>

        {/* Sunrise/Sunset */}
        <div className="flex items-center justify-center gap-8 mb-6 py-3 border-y border-white/5">
          <div className="flex items-center gap-2">
            <Sunrise className="w-5 h-5 text-orange-400" />
            <div>
              <p className="text-white font-bold">{formatTime(weather.daily.sunrise[0])}</p>
              <p className="text-[#8a948a] text-xs">Sunrise</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Sunset className="w-5 h-5 text-purple-400" />
            <div>
              <p className="text-white font-bold">{formatTime(weather.daily.sunset[0])}</p>
              <p className="text-[#8a948a] text-xs">Sunset</p>
            </div>
          </div>
        </div>

        {/* 7-Day Forecast */}
        {expanded && (
          <div>
            <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-wider text-white mb-4">
              7-Day Forecast
            </h3>
            <div className="space-y-2">
              {weather.daily.time.map((date, i) => (
                <div key={date} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                  <div className="flex items-center gap-3 w-28">
                    <span className="text-white text-sm">
                      {i === 0 ? 'Today' : new Date(date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {getWeatherIcon(weather.hourly.weather_code[i * 24 + 12], 'w-6 h-6')}
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-blue-400">{Math.round(weather.daily.temperature_2m_min[i])}°</span>
                    <div className="w-16 h-1.5 bg-[#141714] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-blue-400 to-orange-400 rounded-full"
                        style={{
                          width: `${((weather.daily.temperature_2m_max[i] - weather.daily.temperature_2m_min[i]) / 20) * 100}%`,
                          marginLeft: `${((weather.daily.temperature_2m_min[i] + 5) / 35) * 100}%`,
                        }}
                      />
                    </div>
                    <span className="text-orange-400">{Math.round(weather.daily.temperature_2m_max[i])}°</span>
                  </div>
                  <div className="flex items-center gap-1 w-16 justify-end">
                    <Droplets className="w-3 h-3 text-blue-400" />
                    <span className="text-[#8a948a] text-sm">{weather.daily.precipitation_probability_max[i]}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Hourly Preview (when not expanded) */}
        {!expanded && (
          <div>
            <h3 className="text-[#8a948a] text-sm uppercase tracking-wider mb-3">Next 12 Hours</h3>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {weather.hourly.time.slice(0, 12).map((time, i) => (
                <div key={time} className="flex-shrink-0 text-center">
                  <p className="text-[#8a948a] text-xs mb-2">
                    {i === 0 ? 'Now' : new Date(time).toLocaleTimeString('en-GB', { hour: '2-digit' })}
                  </p>
                  {getWeatherIcon(weather.hourly.weather_code[i], 'w-6 h-6 mx-auto')}
                  <p className="text-white text-sm mt-1">{Math.round(weather.hourly.temperature_2m[i])}°</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Last Updated */}
        {lastUpdated && (
          <p className="text-[#4a504a] text-xs text-center mt-4" data-testid="weather-last-updated">
            Updated {formatLastUpdated(lastUpdated)}
          </p>
        )}
      </div>
    </motion.div>
  );
};

export default WeatherWidget;
